import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { DialogComponent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';
export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  get productPageUrl() {
    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Sets up quantity-based price updating for the modal
   * @param {HTMLElement} modalContent - The modal content element
   */
  #setupQuantityPriceUpdater(modalContent) {
    const quantitySelector = modalContent.querySelector('quantity-selector-component');
    const priceElement = modalContent.querySelector('product-price .price');
    
    if (!quantitySelector || !priceElement) return;

    let unitPrice = 0;
    let lastQuantity = 1;

    const extractPrice = (text) => {
      const match = text.match(/([\d,]+\.?\d*)/);
      return match ? parseFloat(match[1].replace(/,/g, '')) : 0;
    };

    const formatPrice = (price, template) => {
      const match = template.match(/([\d,]+\.?\d*)/);
      if (!match) return template;
      
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(price);
      
      return template.replace(match[1], formatted);
    };

    const updatePrice = () => {
      const quantityInput = quantitySelector.querySelector('input[type="number"]');
      const currentQuantity = quantityInput instanceof HTMLInputElement ? parseInt(quantityInput.value) || 1 : 1;
      
      if (currentQuantity === lastQuantity) return;
      
      if (unitPrice > 0 && currentQuantity > 0) {
        const totalPrice = unitPrice * currentQuantity;
        const currentPriceText = priceElement.textContent?.trim() || '';
        if (currentPriceText) {
          priceElement.textContent = formatPrice(totalPrice, currentPriceText);
        }
      }
      
      lastQuantity = currentQuantity;
    };

    // Initialize unit price
    const initialPriceText = priceElement.textContent?.trim();
    if (initialPriceText) {
      unitPrice = extractPrice(initialPriceText);
    }

    // Listen for quantity changes
    modalContent.addEventListener(ThemeEvents.quantitySelectorUpdate, updatePrice);
    
    // Also listen for direct input changes as backup
    const quantityInput = quantitySelector.querySelector('input[type="number"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.addEventListener('input', updatePrice);
      quantityInput.addEventListener('change', updatePrice);
    }

    // Listen for button clicks
    const quantityButtons = quantitySelector.querySelectorAll('button');
    quantityButtons.forEach(button => {
      button.addEventListener('click', () => setTimeout(updatePrice, 10));
    });
  }

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use
      this.#cachedProductHtml = html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Handles variant update inside the quick-add modal by re-fetching and re-rendering
   */
  #handleQuickAddVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const newHtml = event.detail.data.html;
    if (newHtml) this.updateQuickAddModal(newHtml);
  };

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productDescription = productGrid.querySelector('.text-block--product-description');
      
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      
      if (productDescription) {
        productGrid.appendChild(productDescription);
      }
      
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
      
      // Enable all slideshow slides and controls for mobile
      const mediaGallery = productGrid.querySelector('media-gallery');
      if (mediaGallery) {
        const slideshowSlides = mediaGallery.querySelectorAll('slideshow-slide');
        slideshowSlides.forEach(slide => {
          slide.removeAttribute('aria-hidden');
          if (slide instanceof HTMLElement) {
            slide.style.display = '';
          }
        });
        
        const slideshowControls = mediaGallery.querySelector('slideshow-controls');
        if (slideshowControls instanceof HTMLElement) {
          slideshowControls.style.display = 'flex';
        }
      }
    }

    morph(modalContent, productGrid);
    
    // Rebind variant update to refresh modal on variant changes
    modalContent.removeEventListener(ThemeEvents.variantUpdate, this.#handleQuickAddVariantUpdate);
    modalContent.addEventListener(ThemeEvents.variantUpdate, this.#handleQuickAddVariantUpdate);

    // Setup quantity-based price updating for all devices
    this.#setupQuantityPriceUpdater(modalContent);
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
